import * as React from 'react'
import { v4 as uuid } from 'uuid';
import styled, { css } from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 244px;
  margin-top: 64px;
  margin-left: 28px;
  color: #1a1a1a;
  position: sticky;
  top: 32px;
  height: fit-content;
  min-width: 200px;

  @media screen and (max-width: 767px) {
    display: none;
  }
`

const TableOfContentsBody = styled.div`
  height: 100%;
  width: 100%;
  margin: 8px 0;
  font-size: 13px;
  max-height: calc(100vh - 96px);
  overflow-x: hidden;
  overflow-y: auto;
`

const TableOfContentSection = styled.section<{ isActive: boolean; level: number }>`
  padding-top: 6px;
  padding-bottom: 6px;
  padding-left: ${({ level }) => 16 + (level - 1) * 8}px;
  padding-right: 16px;
  border-color: #f2f2f2;
  border-style: solid;
  border-inline-start-width: 2px;
  border-inline-end-width: 0px;
  border-top: none;
  border-bottom: none;
  color: #737373;

  ${({ isActive }) => isActive && css`
    color: #1a1a1a;
    border-color: #737373;
  `}

  a {
    color: currentColor;
    text-decoration: none;
  }
`

interface Item {
  id: string;
  text: string;
  level: number;
  element: HTMLElement;
}

export const generateTableOfContents = (contentElement: HTMLElement): Item[] => {
  if (!contentElement) return [];

  // Find all heading elements (h1-h6)
  const headings = contentElement.querySelectorAll('h1, h2, h3, h4, h5, h6');

  return Array.from(headings).map((heading: HTMLElement) => {
    // Get heading level from tag name (h1 -> 1, h2 -> 2, etc.)
    const level = parseInt(heading.tagName.substring(1), 10);

    // Generate an ID if none exists
    if (!heading.id) heading.id = `h_${uuid()}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');

    return {
      id: heading.id,
      text: heading.textContent,
      level: level,
      element: heading
    };
  });
};

interface DynamicTableOfContentsProps {
  contentRef: React.RefObject<HTMLElement>;
}

const TableOfContent: React.FC<DynamicTableOfContentsProps> = ({ contentRef }) => {
  const [items, setItems] = React.useState([]);
  const [activeId, setActiveId] = React.useState('');

  React.useEffect(() => {
    if (!contentRef.current) return;

    // Generate TOC items from content
    const items = generateTableOfContents(contentRef.current);
    setItems(items);
    setActiveId(items[0]?.id || '');

    // Setup intersection observer to detect active section
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      {
        rootMargin: '40px 0px -95% 0px',
        threshold: 0
      }
    );

    items.forEach((item) => {
      if (item.element) {
        observer.observe(item.element);
      }
    });

    return () => {
      items.forEach((item) => {
        if (item.element) {
          observer.unobserve(item.element);
        }
      });
    };
  }, [contentRef.current]);

  if (items.length === 0) return null;

  const onItemClick = (id: string) => {
    const element = document.getElementById(id);
    if (element) element.scrollIntoView({ behavior: 'instant', block: 'start', inline: 'nearest' });
    setActiveId(id);
  }

  return (
    <Container>
      <TableOfContentsBody>
        {items.map((item) => (
          <TableOfContentSection key={item.id} level={item.level} isActive={activeId === item.id}>
            <a href='javascript://' onClick={() => onItemClick(item.id)}>{item.text}</a>
          </TableOfContentSection>
        ))}
      </TableOfContentsBody>
    </Container>
  );
};

export default TableOfContent